import {
  AbstractBaseUser,
  AbstractSession,
  getSessionInstructor,
  Role,
} from "@kvix/shared";
import { Placeholder, Text } from "@kvix/ui";
import {
  Box,
  Container,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";
import i18next, { TFunction } from "i18next";
import queryString from "query-string";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, useHistory, useParams } from "react-router";
import { Link } from "react-router-dom";
import { KvixUserContext } from "../../../contexts/user";
import { useIsInActiveRunningCompetition } from "../../../hooks/competition";
import { useFetchSession } from "../../../hooks/http";
import { Footer } from "../../Footer";
import { MetaData } from "../../partials/Meta";
import { Page } from "../../partials/Page";
import { PremiumVideoPlaceholder } from "../../partials/PremiumVideoPlaceholder/PremiumVideoPlaceholder";
import { useUserAccess } from "../../PurchaseFlow";
import { ReplayContent } from "../Replay/Content";
import StreamerVoteButton from "../Streamer/StreamerVoteButton";

const getName = (streamer: AbstractBaseUser) =>
  streamer.name.custom || `${streamer.name.first} ${streamer.name.last}`;

const useStyles = makeStyles((theme) => ({
  displayMoreButton: {
    textTransform: "none",
    height: "36px",
    padding: "0 14px",
    marginTop: theme.spacing(2),
  },
  heartContainer: {
    "& .MuiIconButton-root": {
      padding: "5px 12px 12px 12px",
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: 12,
    },
  },
  presentationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      justifyContent: "start",
      alignItems: "start",
    },
  },
  streamTitle: {
    overflowWrap: "break-word",
    hyphens: "auto",
  },
}));

const ReplayPresentation: React.FC<{
  session: AbstractSession;
  theme: Theme;
  t: TFunction;
}> = (props) => {
  const { session, theme, t } = props;
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const classes = useStyles();
  const instructor = getSessionInstructor(session);
  const voteActive = useIsInActiveRunningCompetition(instructor);
  const checkIfInstructor = instructor.roles.includes(Role.Instructor);
  return (
    <Box py={4}>
      <Text variant="h2" gutterBottom className={classes.streamTitle}>
        {session.title}
      </Text>

      <div className={classes.presentationContainer}>
        <Text style={{ lineHeight: 1.5 }}>
          {t("replay:with")}
          <Link
            to={
              checkIfInstructor
                ? `/instructor/${instructor.id}`
                : `/streamer/${instructor.id}`
            }
          >
            <span style={{ fontWeight: 500 }}>
              {t("replay:streamer", {
                name: getName(instructor),
              })}
            </span>
            <span style={{ display: isMobile ? "block" : "inline" }}>
              {t("replay:wasLiveTimeAgo", {
                time:
                  session &&
                  formatDistanceToNow(new Date(session.start), {
                    locale: i18next.language === "sv-SE" && sv,
                  }),
              })}
            </span>
          </Link>
        </Text>
        <span className={classes.heartContainer}>
          <StreamerVoteButton streamer={instructor} voteActive={voteActive} />
        </span>
      </div>
    </Box>
  );
};

export default () => {
  const params = useParams();
  const sessionId = params["sessionId"];
  const { t, ready } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const session = useFetchSession(sessionId);
  const { user } = useContext(KvixUserContext);
  const [isReplay, setIsReplay] = useState(null);
  const { channel } = queryString.parse(history.location.search);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [sessionId]);

  if (!session.loading && !session.value) {
    return <Redirect to="/page-not-found" />;
  }

  const metaData = useMemo(() => {
    if (!session.value) {
      return null;
    }

    const instructor = getSessionInstructor(session?.value);

    const { first, last, custom } = instructor?.name;

    return {
      title: session.value.title,
      ogTitle: session.value.title,
      description: t("session:metaDescription", {
        title: session.value.title,
        instructor: custom || `${first} ${last}`,
      }),
      ogImage: session.value.thumbnailUrl || instructor.imageUrl,
    } as MetaData;
  }, [session]);

  useEffect(() => {
    setIsReplay(session.value?.mp4Url || session.value?.hlsUrl);
  }, [session]);

  const hasAccess = useUserAccess(
    user,
    user && session && session.value && !session.value.requiredProductAnytime
  );

  if (!ready) {
    return null;
  }

  return (
    <>
      {session.value && (
        <Page meta={metaData}>
          {isReplay === false && channel && (
            <Redirect to={`/channel/${channel}`} />
          )}

          {isReplay && (
            <>
              <Container maxWidth="lg">
                {session.value ? (
                  <>
                    <ReplayPresentation
                      session={session.value}
                      theme={theme}
                      t={t}
                    />
                    {hasAccess ? (
                      <ReplayContent
                        replay={session.value}
                        imageUrl={
                          getSessionInstructor(session?.value)?.imageUrl
                        }
                        name={getSessionInstructor(session?.value)?.name}
                        title={session.value.title}
                      />
                    ) : (
                      <PremiumVideoPlaceholder
                        session={session.value}
                        user={user}
                      />
                    )}
                  </>
                ) : (
                  <Placeholder.Replay />
                )}
              </Container>
              <Footer />
            </>
          )}
        </Page>
      )}
    </>
  );
};
